/**
 * useEntityTree - Hook para gerenciar a arvore de entidades compartilhada entre steps do wizard.
 *
 * A arvore mantém entidades (com tempId para novas, guid para existentes)
 * e relacionamentos entre elas. O processador final resolve tempIds em GUIDs reais.
 *
 * Persistencia via sessionStorage para sobreviver a refreshes acidentais.
 */

import { useState, useCallback, useRef } from 'react';

const STORAGE_KEY = 'agendario_wizard_entity_tree';

let tempIdCounter = 0;

/** Gera um ID temporario unico para entidades novas */
export const generateTempId = () => `temp_${Date.now()}_${++tempIdCounter}`;

/** Verifica se um ID e temporario (entidade ainda nao criada) */
export const isTempId = (id) => typeof id === 'string' && id.startsWith('temp_');

/** Estado inicial da arvore */
const createEmptyTree = () => ({
  professionals: [],   // { id: tempId|guid, name, isNew: bool }
  services: [],        // { id: tempId|guid, name, category?, duration?, price?, isNew: bool }
  places: [],          // { id: tempId|guid, name, isNew: bool }

  // Relacionamentos
  links: {
    placeProfessionals: [],      // { place: id, professional: id }
    placeServices: [],           // { place: id, service: id }
    placeServiceProfessionals: [], // { place: id, service: id, professional: id }
  },

  // Agendas
  schedules: [],  // { place: id, professional: id, name: string, shifts: [...] }
});

/**
 * Hook principal para gerenciar a arvore de entidades.
 * @param {Object} options
 * @param {boolean} options.persist - Se true, persiste em sessionStorage (default: true)
 */
export const useEntityTree = ({ persist = true } = {}) => {
  const [tree, setTree] = useState(() => {
    if (persist) {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          console.log('[useEntityTree] Restaurando arvore do sessionStorage');
          return parsed;
        }
      } catch (e) {
        console.warn('[useEntityTree] Erro ao restaurar sessionStorage:', e);
      }
    }
    return createEmptyTree();
  });

  // Ref para evitar re-renders desnecessarios no save
  const saveTimeoutRef = useRef(null);

  // Salvar no sessionStorage com debounce
  const saveToStorage = useCallback((newTree) => {
    if (!persist) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newTree));
      } catch (e) {
        console.warn('[useEntityTree] Erro ao salvar sessionStorage:', e);
      }
    }, 300);
  }, [persist]);

  // Helper para atualizar tree + salvar
  const updateTree = useCallback((updater) => {
    setTree(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveToStorage(next);
      return next;
    });
  }, [saveToStorage]);

  // ==================== PROFESSIONALS ====================

  const addProfessional = useCallback((name, existingId = null) => {
    const id = existingId || generateTempId();
    const isNew = !existingId;
    updateTree(prev => {
      // Evitar duplicatas
      if (prev.professionals.some(p => p.id === id || p.name.toLowerCase() === name.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        professionals: [...prev.professionals, { id, name, isNew }],
      };
    });
    return id;
  }, [updateTree]);

  const removeProfessional = useCallback((id) => {
    updateTree(prev => ({
      ...prev,
      professionals: prev.professionals.filter(p => p.id !== id),
      links: {
        ...prev.links,
        placeProfessionals: prev.links.placeProfessionals.filter(l => l.professional !== id),
        placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(l => l.professional !== id),
      },
      schedules: prev.schedules.filter(s => s.professional !== id),
    }));
  }, [updateTree]);

  const toggleProfessional = useCallback((id) => {
    updateTree(prev => ({
      ...prev,
      professionals: prev.professionals.map(p =>
        p.id === id ? { ...p, selected: !p.selected } : p
      ),
    }));
  }, [updateTree]);

  // ==================== SERVICES ====================

  const addService = useCallback((name, existingId = null, extra = {}) => {
    const id = existingId || generateTempId();
    const isNew = !existingId;
    updateTree(prev => {
      if (prev.services.some(s => s.id === id || s.name.toLowerCase() === name.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        services: [...prev.services, { id, name, isNew, ...extra }],
      };
    });
    return id;
  }, [updateTree]);

  const removeService = useCallback((id) => {
    updateTree(prev => ({
      ...prev,
      services: prev.services.filter(s => s.id !== id),
      links: {
        ...prev.links,
        placeServices: prev.links.placeServices.filter(l => l.service !== id),
        placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(l => l.service !== id),
      },
    }));
  }, [updateTree]);

  // ==================== PLACES ====================

  const addPlace = useCallback((name, existingId = null) => {
    const id = existingId || generateTempId();
    const isNew = !existingId;
    updateTree(prev => {
      if (prev.places.some(p => p.id === id || p.name.toLowerCase() === name.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        places: [...prev.places, { id, name, isNew }],
      };
    });
    return id;
  }, [updateTree]);

  const removePlace = useCallback((id) => {
    updateTree(prev => ({
      ...prev,
      places: prev.places.filter(p => p.id !== id),
      links: {
        ...prev.links,
        placeProfessionals: prev.links.placeProfessionals.filter(l => l.place !== id),
        placeServices: prev.links.placeServices.filter(l => l.place !== id),
        placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(l => l.place !== id),
      },
      schedules: prev.schedules.filter(s => s.place !== id),
    }));
  }, [updateTree]);

  // ==================== LINKS ====================

  const linkProfessionalToPlace = useCallback((placeId, professionalId) => {
    updateTree(prev => {
      const exists = prev.links.placeProfessionals.some(
        l => l.place === placeId && l.professional === professionalId
      );
      if (exists) return prev;
      return {
        ...prev,
        links: {
          ...prev.links,
          placeProfessionals: [...prev.links.placeProfessionals, { place: placeId, professional: professionalId }],
        },
      };
    });
  }, [updateTree]);

  const unlinkProfessionalFromPlace = useCallback((placeId, professionalId) => {
    updateTree(prev => ({
      ...prev,
      links: {
        ...prev.links,
        placeProfessionals: prev.links.placeProfessionals.filter(
          l => !(l.place === placeId && l.professional === professionalId)
        ),
        placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(
          l => !(l.place === placeId && l.professional === professionalId)
        ),
      },
    }));
  }, [updateTree]);

  const linkServiceToPlace = useCallback((placeId, serviceId) => {
    updateTree(prev => {
      const exists = prev.links.placeServices.some(
        l => l.place === placeId && l.service === serviceId
      );
      if (exists) return prev;
      return {
        ...prev,
        links: {
          ...prev.links,
          placeServices: [...prev.links.placeServices, { place: placeId, service: serviceId }],
        },
      };
    });
  }, [updateTree]);

  const linkProfessionalToServiceAtPlace = useCallback((placeId, serviceId, professionalId) => {
    updateTree(prev => {
      const exists = prev.links.placeServiceProfessionals.some(
        l => l.place === placeId && l.service === serviceId && l.professional === professionalId
      );
      if (exists) return prev;
      return {
        ...prev,
        links: {
          ...prev.links,
          placeServiceProfessionals: [
            ...prev.links.placeServiceProfessionals,
            { place: placeId, service: serviceId, professional: professionalId },
          ],
        },
      };
    });
  }, [updateTree]);

  // ==================== SCHEDULES ====================

  const addSchedule = useCallback((placeId, professionalId, config = {}) => {
    updateTree(prev => {
      const exists = prev.schedules.some(
        s => s.place === placeId && s.professional === professionalId
      );
      if (exists) {
        // Atualiza config existente
        return {
          ...prev,
          schedules: prev.schedules.map(s =>
            (s.place === placeId && s.professional === professionalId)
              ? { ...s, ...config }
              : s
          ),
        };
      }
      return {
        ...prev,
        schedules: [...prev.schedules, { place: placeId, professional: professionalId, shifts: [], ...config }],
      };
    });
  }, [updateTree]);

  const updateScheduleShifts = useCallback((placeId, professionalId, shifts) => {
    updateTree(prev => ({
      ...prev,
      schedules: prev.schedules.map(s =>
        (s.place === placeId && s.professional === professionalId)
          ? { ...s, shifts }
          : s
      ),
    }));
  }, [updateTree]);

  // ==================== BULK / UTILITY ====================

  const reset = useCallback(() => {
    const empty = createEmptyTree();
    setTree(empty);
    if (persist) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [persist]);

  /** Carrega entidades existentes da API para selecao no wizard */
  const loadExisting = useCallback((professionals = [], services = [], places = [], links = {}) => {
    updateTree(prev => ({
      ...prev,
      professionals: [
        ...professionals.map(p => ({ id: p.id, name: p.name, isNew: false, selected: false })),
        ...prev.professionals.filter(p => p.isNew),
      ],
      services: [
        ...services.map(s => ({ id: s.id, name: s.name, isNew: false, category: s.category, duration: s.duration, price: s.price })),
        ...prev.services.filter(s => s.isNew),
      ],
      places: [
        ...places.map(p => ({ id: p.id, name: p.name, isNew: false })),
        ...prev.places.filter(p => p.isNew),
      ],
      links: {
        placeProfessionals: links.placeProfessionals || [],
        placeServices: links.placeServices || [],
        placeServiceProfessionals: links.placeServiceProfessionals || [],
      },
    }));
  }, [updateTree]);

  /** Retorna os profissionais selecionados (marcados como selected) */
  const getSelectedProfessionals = useCallback(() => {
    return tree.professionals.filter(p => p.selected);
  }, [tree.professionals]);

  /** Retorna os servicos vinculados a um profissional em um place */
  const getServicesForProfessionalAtPlace = useCallback((placeId, professionalId) => {
    const serviceIds = tree.links.placeServiceProfessionals
      .filter(l => l.place === placeId && l.professional === professionalId)
      .map(l => l.service);
    return tree.services.filter(s => serviceIds.includes(s.id));
  }, [tree]);

  return {
    tree,
    setTree: updateTree,

    // Professionals
    addProfessional,
    removeProfessional,
    toggleProfessional,
    getSelectedProfessionals,

    // Services
    addService,
    removeService,

    // Places
    addPlace,
    removePlace,

    // Links
    linkProfessionalToPlace,
    unlinkProfessionalFromPlace,
    linkServiceToPlace,
    linkProfessionalToServiceAtPlace,

    // Schedules
    addSchedule,
    updateScheduleShifts,

    // Utility
    reset,
    loadExisting,
    getServicesForProfessionalAtPlace,
  };
};

export default useEntityTree;
